/**
 * Edits the playground makes to a generated site WITHOUT a model call: the
 * text of an element picked in the preview, and the design tokens on `:root`.
 *
 * Lovable's visual edits and theme panel are free because they never ask a
 * model; these are the same, and deterministic. Each function refuses rather
 * than guesses: an element that holds other elements, text that occurs more
 * than once, or a token that is not declared all come back as a reason, and
 * the caller hands the change to the agent instead.
 *
 * Pure: strings in, strings out.
 */

export type EditResult = { readonly ok: true; readonly content: string } | { readonly ok: false; readonly reason: string };

/** Escape text for an HTML text node. */
export function escapeText(text: string): string {
  return text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Replace the text of the element carrying `data-tavsin-id="<id>"`.
 * Only a leaf (no child elements) is edited; anything else is refused.
 */
export function setTextById(html: string, id: string, text: string): EditResult {
  if (!/^[A-Za-z0-9_.:-]{1,80}$/.test(id)) return { ok: false, reason: "not an editable element id" };
  const open = new RegExp(`<([A-Za-z][A-Za-z0-9-]*)\\b[^>]*\\sdata-tavsin-id=["']${escapeRegExp(id)}["'][^>]*>`, "g");
  const matches = [...html.matchAll(open)];
  if (matches.length === 0) return { ok: false, reason: "that element is not in this file" };
  if (matches.length > 1) return { ok: false, reason: "more than one element carries that id" };
  const m = matches[0]!;
  const tag = m[1]!.toLowerCase();
  const start = m.index + m[0].length;
  const close = html.toLowerCase().indexOf(`</${tag}>`, start);
  if (close < 0) return { ok: false, reason: "the element is not closed" };
  const inner = html.slice(start, close);
  if (/<[A-Za-z]/.test(inner)) return { ok: false, reason: "the element contains other elements — ask Tavsin to change it" };
  return { ok: true, content: `${html.slice(0, start)}${escapeText(text)}${html.slice(close)}` };
}

/**
 * Replace a piece of visible text that occurs exactly once in the file.
 * For sites written without `data-tavsin-id`. Whitespace runs in `oldText`
 * match any whitespace run in the source, since the browser collapses them.
 */
export function replaceUniqueText(html: string, oldText: string, newText: string): EditResult {
  const needle = oldText.trim();
  if (needle.length < 2) return { ok: false, reason: "the selected text is too short to find safely" };
  const pattern = new RegExp(needle.split(/\s+/).map((part) => escapeRegExp(escapeText(part))).join("\\s+"), "g");
  const hits = [...html.matchAll(pattern)].filter((h) => {
    // Only text between tags, never inside a tag or an attribute.
    const before = html.slice(0, h.index);
    return before.lastIndexOf(">") >= before.lastIndexOf("<");
  });
  if (hits.length === 0) return { ok: false, reason: "that text is not in this file as written (it may be generated by a script)" };
  if (hits.length > 1) return { ok: false, reason: `that text appears ${String(hits.length)} times — ask Tavsin to change the one you mean` };
  const h = hits[0]!;
  return { ok: true, content: `${html.slice(0, h.index)}${escapeText(newText.trim())}${html.slice(h.index + h[0].length)}` };
}

export interface DesignToken {
  readonly name: string;
  readonly value: string;
}

/** The first `:root { … }` block's custom properties, in source order. */
export function readTokens(css: string): DesignToken[] {
  const block = firstRootBlock(css);
  if (!block) return [];
  const out: DesignToken[] = [];
  for (const m of block.body.matchAll(/(--[A-Za-z0-9_-]+)\s*:\s*([^;]+);/g)) out.push({ name: m[1]!, value: m[2]!.trim() });
  return out;
}

/** Set token values in the first `:root` block. Unknown names are refused. */
export function writeTokens(css: string, updates: Readonly<Record<string, string>>): EditResult {
  const block = firstRootBlock(css);
  if (!block) return { ok: false, reason: "no :root block with design tokens in this file" };
  let body = block.body;
  for (const [name, value] of Object.entries(updates)) {
    if (!/^--[A-Za-z0-9_-]+$/.test(name)) return { ok: false, reason: `not a token name: ${name}` };
    if (/[;{}]/.test(value) || value.trim() === "") return { ok: false, reason: `not a valid value for ${name}` };
    const re = new RegExp(`(${escapeRegExp(name)}\\s*:\\s*)([^;]+)(;)`);
    if (!re.test(body)) return { ok: false, reason: `${name} is not declared in :root` };
    body = body.replace(re, (_all, a: string, _old: string, c: string) => `${a}${value.trim()}${c}`);
  }
  return { ok: true, content: `${css.slice(0, block.start)}${body}${css.slice(block.end)}` };
}

function firstRootBlock(css: string): { start: number; end: number; body: string } | null {
  const m = /:root\s*\{/.exec(css);
  if (!m) return null;
  const start = m.index + m[0].length;
  let depth = 1;
  for (let i = start; i < css.length; i += 1) {
    const ch = css[i];
    if (ch === "{") depth += 1;
    else if (ch === "}") {
      depth -= 1;
      if (depth === 0) return { start, end: i, body: css.slice(start, i) };
    }
  }
  return null;
}

/**
 * Keep `tavsin/design.md` in step with a theme change. The contract lists
 * each palette token as "- `name`: #hex"; a colour changed in the theme panel
 * but not here would make the agent's next edit "off-palette". Lines for
 * tokens that are not listed are left alone; the contract's prose is kept.
 */
export function syncContractPalette(md: string, updates: Readonly<Record<string, string>>): string {
  let out = md;
  for (const [name, value] of Object.entries(updates)) {
    if (!isHexColor(value)) continue;
    const key = escapeRegExp(name.replace(/^--/, ""));
    out = out.replace(new RegExp(`^(\\s*-\\s*\`${key}\`\\s*:\\s*)#[0-9a-fA-F]{3,8}`, "m"), `$1${value.trim()}`);
  }
  return out;
}

/** True for a value a colour input can show (#rgb or #rrggbb). */
export function isHexColor(value: string): boolean {
  return /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(value.trim());
}

/** The project path of a page shown in the preview frame, from the frame's URL path. */
export function previewPagePath(pathname: string): string {
  let rest = pathname
    .replace(/^\/preview\/tp\/[^/]+\/[^/]+\/?/, "")
    .replace(/^\/preview\/t\/[^/]+\/?/, "")
    .replace(/^\/preview\/?/, "");
  try {
    rest = decodeURIComponent(rest);
  } catch {
    /* keep it as is */
  }
  if (rest === "" || rest.endsWith("/")) rest = `${rest}index.html`;
  return rest;
}
